import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileCheck, ClipboardList, Users, CheckCircle2, XCircle, ArrowLeft, Award, TrendingUp,
  Zap, FileSpreadsheet, FileText, Loader2, RefreshCw, History,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// =========================================================================
// HOSPITAL ADMIN - Exam Dashboard
// =========================================================================
export function OrgExamDashboardPage() {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/exam-dashboard");
        setTotals(data.data.totals);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <DashboardLayout title="Exams"><LoadingBlock /></DashboardLayout>;
  if (!totals) return null;
  return (
    <DashboardLayout title="Exam Overview" breadcrumbs={["Hospital Admin", "Exams"]}>
      <div className="space-y-6" data-testid="org-exam-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Assignments" value={totals.assignments} icon="ClipboardList" tone="navy" testId="oed-kpi-a" index={0} />
          <StatCard label="Attempts" value={totals.attempts} icon="FileCheck" tone="slate" testId="oed-kpi-at" index={1} />
          <StatCard label="Passed" value={totals.passed} icon="CircleCheckBig" tone="green" testId="oed-kpi-p" index={2} />
          <StatCard label="Failed" value={totals.failed} icon="X" tone="rose" testId="oed-kpi-f" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="In progress" value={totals.in_progress} icon="Clock" tone="amber" testId="oed-kpi-ip" index={4} />
          <StatCard label="Pending" value={totals.pending} icon="Hourglass" tone="slate" testId="oed-kpi-pd" index={5} />
        </section>
        <div className="grid gap-4 sm:grid-cols-3">
          <Link to="/app/hospital/exams" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md">
            <FileCheck className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Available exams</div>
            <div className="text-xs text-slate-500">Assign to your staff</div>
          </Link>
          <Link to="/app/hospital/exam-assignments" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md">
            <ClipboardList className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Assignments</div>
            <div className="text-xs text-slate-500">Track staff exam status</div>
          </Link>
          <Link to="/app/hospital/exam-results" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md">
            <Award className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Exam results</div>
            <div className="text-xs text-slate-500">Attempt-wise performance</div>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// HOSPITAL ADMIN - Available Exams + Assign
// =========================================================================
export function OrgExamListPage() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [assignExam, setAssignExam] = useState(null);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState({ department_id: "", user_ids: [], scope: "individual" });
  const [assigning, setAssigning] = useState(false);
  // Re-exam dialog state (Institute Admin only)
  const [reExamExam, setReExamExam] = useState(null);
  const [reEligible, setReEligible] = useState([]);
  const [reEligLoading, setReEligLoading] = useState(false);
  const [reSelected, setReSelected] = useState({});
  const [reRunning, setReRunning] = useState(false);
  const [reAutoSubmit, setReAutoSubmit] = useState(false);
  const [reDate, setReDate] = useState(""); // optional YYYY-MM-DD backdate
  const [reRange, setReRange] = useState({ min: 90, max: 95 });
  const [reResult, setReResult] = useState(null);

  // Re-exam audit log dialog state
  const [logOpen, setLogOpen] = useState(false);
  const [logFrom, setLogFrom] = useState("");
  const [logTo, setLogTo] = useState("");
  const [logExamId, setLogExamId] = useState("");
  const [logBusy, setLogBusy] = useState({ xlsx: false, pdf: false });
  const [logDates, setLogDates] = useState([]);
  const [logDatesLoading, setLogDatesLoading] = useState(false);
  const [logDateBusy, setLogDateBusy] = useState({}); // key: `${date}-${fmt}` → bool

  // Bulk auto-submit dialog state (Hospital Admin only)
  const [autoExam, setAutoExam] = useState(null);
  const [autoRange, setAutoRange] = useState({ min: 90, max: 95 });
  const [autoDate, setAutoDate] = useState(""); // optional YYYY-MM-DD backdate
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [topicBusy, setTopicBusy] = useState({});
  const [topicPdfBusy, setTopicPdfBusy] = useState({});
  // Preview list of pending staff — admin unticks anyone they want to
  // exclude before running the auto-submit.
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingStaff, setPendingStaff] = useState([]);
  const [alreadyPassed, setAlreadyPassed] = useState([]);
  const [inactiveStaff, setInactiveStaff] = useState([]);
  const [selectedPending, setSelectedPending] = useState({}); // {user_id: true}
  const [previewQuery, setPreviewQuery] = useState("");

  const openAutoDialog = async (exam) => {
    setAutoExam(exam);
    setAutoRange({ min: 90, max: 95 });
    setAutoDate("");
    setAutoResult(null);
    setPreviewQuery("");
    setPendingStaff([]);
    setAlreadyPassed([]);
    setInactiveStaff([]);
    setSelectedPending({});
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/org/exams/${exam.id}/auto-submit/pending`);
      const p = data.data?.pending || [];
      const ap = data.data?.already_passed || [];
      const inact = data.data?.inactive || [];
      setPendingStaff(p);
      setAlreadyPassed(ap);
      setInactiveStaff(inact);
      // Preselect every pending user by default
      const pre = {}; p.forEach((s) => { pre[s.user_id] = true; });
      setSelectedPending(pre);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPreviewLoading(false); }
  };

  const togglePending = (uid) => setSelectedPending((prev) => ({ ...prev, [uid]: !prev[uid] }));
  const setAllPending = (val) => {
    const next = {};
    filteredPending.forEach((s) => { next[s.user_id] = val; });
    setSelectedPending((prev) => ({ ...prev, ...next }));
  };
  const filteredPending = (() => {
    const q = previewQuery.trim().toLowerCase();
    if (!q) return pendingStaff;
    return pendingStaff.filter((s) =>
      (s.full_name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.employee_id || "").toLowerCase().includes(q) ||
      (s.department_name || "").toLowerCase().includes(q)
    );
  })();
  const selectedPendingCount = Object.values(selectedPending).filter(Boolean).length;

  // Re-exam dialog helpers
  const openReExamDialog = async (exam) => {
    setReExamExam(exam);
    setReEligible([]); setReSelected({}); setReEligLoading(true);
    setReAutoSubmit(false); setReDate(""); setReRange({ min: 90, max: 95 });
    setReResult(null);
    try {
      const { data } = await api.get(`/org/exams/${exam.id}/reexam/eligible`);
      setReEligible(data.data.staff || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReEligLoading(false); }
  };
  const toggleReSel = (uid) => setReSelected((p) => ({ ...p, [uid]: !p[uid] }));
  const setAllReSel = (val) => {
    const next = {};
    reEligible.forEach((s) => { next[s.user_id] = val; });
    setReSelected(next);
  };
  const reSelectedCount = Object.values(reSelected).filter(Boolean).length;
  const submitReExam = async () => {
    if (!reExamExam) return;
    const uids = Object.entries(reSelected).filter(([, v]) => v).map(([k]) => k);
    if (!uids.length) return toast.error("Select at least one staff for re-exam.");
    if (reAutoSubmit) {
      const minPct = Number(reRange.min);
      const maxPct = Number(reRange.max);
      if (!minPct || !maxPct || minPct < 1 || maxPct > 100 || minPct > maxPct) {
        return toast.error("Enter valid range (min ≤ max, 1–100).");
      }
    }
    const confirmMsg = reAutoSubmit
      ? `Are you sure? ${uids.length} staff ke previous attempts archive honge AUR turant naye synthetic attempts generate honge${reDate ? ` (date: ${reDate})` : ""}.`
      : `Are you sure? ${uids.length} staff ke previous attempts archive ho jaayenge aur wo dobara exam de sakenge.`;
    if (!window.confirm(confirmMsg)) return;
    setReRunning(true);
    try {
      const payload = {
        user_ids: uids,
        auto_submit: reAutoSubmit,
        submission_date: reDate || null,
      };
      if (reAutoSubmit) {
        payload.min_percent = Number(reRange.min);
        payload.max_percent = Number(reRange.max);
      }
      const { data } = await api.post(`/org/exams/${reExamExam.id}/reexam`, payload);
      const d = data.data || {};
      setReResult(d);
      if (reAutoSubmit && d.auto_submit) {
        toast.success(`Re-exam + auto-submit: ${d.auto_submit.submitted} attempts generated, ${d.staff_count} staff.`);
      } else {
        toast.success(`Re-exam allowed for ${d.staff_count} staff (${d.attempts_archived} attempts archived).`);
      }
      // Refresh counts on the card grid
      const r = await api.get("/org/exams");
      setExams(r.data.data.exams);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReRunning(false); }
  };

  // Re-exam audit log downloads
  const downloadReexamLog = async (fmt, override) => {
    const busyKey = override?.date ? `${override.date}-${fmt}` : null;
    if (busyKey) setLogDateBusy((p) => ({ ...p, [busyKey]: true }));
    else setLogBusy((p) => ({ ...p, [fmt]: true }));
    try {
      const params = {};
      const fromD = override?.date || logFrom;
      const toD = override?.date || logTo;
      if (fromD) params.from_date = fromD;
      if (toD) params.to_date = toD;
      if (logExamId) params.exam_id = logExamId;
      const { data } = await api.get(`/org/reexam-log.${fmt}`, {
        params, responseType: "blob",
      });
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const link = document.createElement("a");
      link.href = url;
      const dateSuffix = override?.date ? `_${override.date}` : "";
      const label = logExamId ? `exam_${logExamId.slice(0, 8)}` : "all_exams";
      link.download = `reexam_log_${label}${dateSuffix}.${fmt}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Re-exam log ${fmt.toUpperCase()}${override?.date ? ` (${override.date})` : ""} downloaded.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      if (busyKey) setLogDateBusy((p) => ({ ...p, [busyKey]: false }));
      else setLogBusy((p) => ({ ...p, [fmt]: false }));
    }
  };

  // Load list of dates whenever filters change while dialog is open
  useEffect(() => {
    if (!logOpen) return;
    (async () => {
      setLogDatesLoading(true);
      try {
        const params = {};
        if (logFrom) params.from_date = logFrom;
        if (logTo) params.to_date = logTo;
        if (logExamId) params.exam_id = logExamId;
        const { data } = await api.get("/org/reexam-log/dates", { params });
        setLogDates(data.data?.dates || []);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLogDatesLoading(false); }
    })();
  }, [logOpen, logFrom, logTo, logExamId]);

  const downloadTopicReport = async (exam) => {
    setTopicBusy((p) => ({ ...p, [exam.id]: true }));
    try {
      const { data } = await api.get(`/org/exams/${exam.id}/topic-report.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `topic_report_${exam.exam_code}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Topic Excel downloaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTopicBusy((p) => ({ ...p, [exam.id]: false })); }
  };

  const downloadTopicPdf = async (exam) => {
    setTopicPdfBusy((p) => ({ ...p, [exam.id]: true }));
    try {
      const { data } = await api.get(`/org/exams/${exam.id}/topic-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `topic_report_${exam.exam_code}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Topic PDF downloaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTopicPdfBusy((p) => ({ ...p, [exam.id]: false })); }
  };

  const submitAutoBulk = async () => {
    if (!autoExam) return;
    const minPct = Number(autoRange.min);
    const maxPct = Number(autoRange.max);
    if (!minPct || !maxPct || minPct < 1 || maxPct > 100 || minPct > maxPct) {
      toast.error("Enter valid range (min ≤ max, 1–100).");
      return;
    }
    const uids = Object.entries(selectedPending).filter(([, v]) => v).map(([k]) => k);
    if (!uids.length) { toast.error("Pick at least one staff member (or all)."); return; }
    setAutoRunning(true);
    setAutoResult(null);
    try {
      const { data } = await api.post(`/org/exams/${autoExam.id}/auto-submit`, {
        user_ids: uids,
        min_percent: minPct,
        max_percent: maxPct,
        submission_date: autoDate || null,
      });
      setAutoResult(data.data);
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAutoRunning(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/exams");
      setExams(data.data.exams);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!assignExam) return;
    (async () => {
      try { const { data } = await api.get("/org/departments"); setDepts(data.data.departments || []); } catch { /* */ }
      try { const { data } = await api.get("/org/staff", { params: { page_size: 500 } }); setUsers(data.data.users || data.data.staff || data.data.rows || []); } catch { /* */ }
    })();
  }, [assignExam]);

  const toggle = (uid) => setTarget((p) => ({ ...p, user_ids: p.user_ids.includes(uid) ? p.user_ids.filter((x) => x !== uid) : [...p.user_ids, uid] }));

  const submitAssign = async () => {
    if (!assignExam) return;
    setAssigning(true);
    try {
      if (target.scope === "all_staff") {
        const { data } = await api.post(`/org/exams/${assignExam.id}/assign/all-staff`, {});
        toast.success(`Assigned to all staff (${data.data.created} created).`);
      } else if (target.scope === "department") {
        const { data } = await api.post(`/org/exams/${assignExam.id}/assign/department`, { department_id: target.department_id });
        toast.success(`Assigned to department (${data.data.created} created).`);
      } else {
        if (!target.user_ids.length) return toast.error("Select at least one user.");
        const { data } = await api.post(`/org/exams/${assignExam.id}/assign/individual`, { user_ids: target.user_ids });
        toast.success(`Assigned to ${data.data.created} users.`);
      }
      setAssignExam(null);
      setTarget({ department_id: "", user_ids: [], scope: "individual" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAssigning(false); }
  };

  return (
    <DashboardLayout title="Available Exams" breadcrumbs={["Hospital Admin", "Exams"]}>
      <div className="space-y-4" data-testid="org-exam-list">
        {/* Search box (client-side filter) */}
        {!loading && exams.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by exam title, code or course…"
              className="max-w-md"
              data-testid="org-exam-search"
            />
            <div className="text-[11px] font-semibold text-slate-500" data-testid="org-exam-count">
              {(() => {
                const q = query.trim().toLowerCase();
                const total = exams.length;
                const shown = q ? exams.filter((e) =>
                  (e.exam_title || "").toLowerCase().includes(q) ||
                  (e.exam_code || "").toLowerCase().includes(q) ||
                  (e.course_title || "").toLowerCase().includes(q) ||
                  (e.course_code || "").toLowerCase().includes(q)
                ).length : total;
                return `${shown} / ${total} exams`;
              })()}
            </div>
            <div className="ml-auto">
              <button type="button"
                onClick={() => { setLogOpen(true); setLogExamId(""); setLogFrom(""); setLogTo(""); }}
                className="inline-flex items-center gap-1.5 rounded-md border-2 border-sky-500 bg-sky-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-sky-800 hover:bg-sky-100"
                data-testid="reexam-log-open"
                title="Download the compliance audit log of every re-exam action">
                <History className="h-3.5 w-3.5" /> Re-exam Log
              </button>
            </div>
          </div>
        )}
        {loading ? <LoadingBlock label="Loading…" /> : exams.length === 0 ? (
          <EmptyState title="No exams available" description="Steward Super Admin has not published any exams for your mapped courses yet." icon={FileCheck} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {exams
              .filter((e) => {
                const q = query.trim().toLowerCase();
                if (!q) return true;
                return (
                  (e.exam_title || "").toLowerCase().includes(q) ||
                  (e.exam_code || "").toLowerCase().includes(q) ||
                  (e.course_title || "").toLowerCase().includes(q) ||
                  (e.course_code || "").toLowerCase().includes(q)
                );
              })
              .map((e) => (
              <div key={e.id}
                   className={`rounded-2xl border overflow-hidden ${e.all_done ? "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200" : "border-slate-200 bg-white"}`}
                   data-testid={`org-exam-${e.id}`}>
                <div className={`h-24 p-4 text-white bg-gradient-to-br ${e.all_done ? "from-emerald-700 to-emerald-500" : "from-[#101876] to-[#329112]"}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs opacity-80 font-bold uppercase tracking-widest">{e.exam_type?.replace(/_/g, " ")}</div>
                    {e.all_done && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`org-exam-done-${e.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> Done
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-bold mt-1 line-clamp-2">{e.exam_title}</div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-xs text-slate-500 font-mono">{e.exam_code}</div>
                  <div className="text-xs text-slate-500">Course: <b>{e.course_title}</b></div>
                  <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
                    <div><b>{e.total_questions_to_display}</b> Qs</div>
                    <div><b>{e.duration_minutes}m</b></div>
                    <div><b>{e.passing_percentage}%</b></div>
                  </div>
                  {(e.assigned_count > 0) && (
                    <div className={`text-[11px] font-bold ${e.all_done ? "text-emerald-700" : "text-slate-600"}`}>
                      {e.passed_count} / {e.assigned_count} passed
                    </div>
                  )}
                  <button onClick={() => setAssignExam(e)} className="mt-2 w-full rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid={`org-assign-${e.id}`}>
                    Assign to staff
                  </button>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => openAutoDialog(e)}
                      className="inline-flex items-center justify-center gap-1 rounded-md border-2 border-amber-500 bg-amber-50 px-2 py-1.5 text-[11px] font-bold text-amber-800 hover:bg-amber-100"
                      data-testid={`org-auto-submit-${e.id}`}
                      title="Auto-submit exam for pending staff with random marks"
                    >
                      <Zap className="h-3 w-3" /> Auto-submit
                    </button>
                    <button
                      onClick={() => openReExamDialog(e)}
                      className="inline-flex items-center justify-center gap-1 rounded-md border-2 border-sky-500 bg-sky-50 px-2 py-1.5 text-[11px] font-bold text-sky-800 hover:bg-sky-100"
                      data-testid={`org-reexam-${e.id}`}
                      title="Allow selected staff to retake this exam"
                    >
                      <RefreshCw className="h-3 w-3" /> Re-exam
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => downloadTopicReport(e)}
                      disabled={!!topicBusy[e.id]}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-emerald-500 bg-white px-2 py-1.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      data-testid={`org-topic-report-${e.id}`}
                      title="Download topic-wise (all staff) report as Excel"
                    >
                      {topicBusy[e.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                      Topic Excel
                    </button>
                    <button
                      onClick={() => downloadTopicPdf(e)}
                      disabled={!!topicPdfBusy[e.id]}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-rose-400 bg-white px-2 py-1.5 text-[11px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                      data-testid={`org-topic-pdf-${e.id}`}
                      title="Print-friendly PDF with all staff in one sheet"
                    >
                      {topicPdfBusy[e.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                      Topic PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!assignExam} onOpenChange={(o) => !o && setAssignExam(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Assign — {assignExam?.exam_title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={target.scope} onValueChange={(v) => setTarget((p) => ({ ...p, scope: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual users</SelectItem>
                <SelectItem value="department">Department</SelectItem>
                <SelectItem value="all_staff">All active staff</SelectItem>
              </SelectContent>
            </Select>
            {target.scope === "department" && (
              <select value={target.department_id} onChange={(e) => setTarget((p) => ({ ...p, department_id: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="">Select department</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.department_name}</option>)}
              </select>
            )}
            {target.scope === "individual" && (
              <div className="rounded-md border border-slate-200 max-h-60 overflow-auto">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={target.user_ids.includes(u.id)} onChange={() => toggle(u.id)} />
                    <span>{u.full_name}</span>
                    <span className="text-xs text-slate-500">{u.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAssignExam(null)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">Cancel</button>
            <button disabled={assigning} onClick={submitAssign} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="org-assign-submit">
              {assigning ? "Assigning…" : "Assign"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Auto-Submit dialog (Institute Admin) */}
      <Dialog open={!!autoExam} onOpenChange={(o) => { if (!o) { setAutoExam(null); setAutoResult(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <Zap className="h-5 w-5" /> Auto-submit — {autoExam?.exam_title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="font-bold uppercase tracking-widest mb-1">⚠ Institute-Admin only</div>
              Neeche selected staff ke liye random marks generate honge. Jinke pehle se passed attempt hain,
              wo already-hide list me hain. Aap kisi ko bhi checkbox se hata sakte ho before running.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Min %</Label>
                <Input type="number" min={1} max={100} value={autoRange.min}
                  onChange={(e) => setAutoRange({ ...autoRange, min: e.target.value })}
                  data-testid="auto-submit-min" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Max %</Label>
                <Input type="number" min={1} max={100} value={autoRange.max}
                  onChange={(e) => setAutoRange({ ...autoRange, max: e.target.value })}
                  data-testid="auto-submit-max" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">
                Submission date <span className="font-normal normal-case text-slate-400">(optional — backdate the whole record)</span>
              </Label>
              <Input type="date" value={autoDate} max={new Date().toISOString().slice(0,10)}
                onChange={(e) => setAutoDate(e.target.value)}
                data-testid="auto-submit-date" />
              <div className="text-[11px] text-slate-500">
                Khali chhodo to aaj ki date use hogi. Koi date select karo to har generated record (started_at, submitted_at, created_at) usi date ka dikhayega. Student self-submission par koi asar nahi.
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              Default: 90–95%. Passing %: <b>{autoExam?.passing_percentage}%</b>, Total marks: <b>{autoExam?.total_marks}</b>. Random duration 5–20 min per attempt.
            </div>

            {/* Preview list of pending staff */}
            {!autoResult && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Pending staff</span>
                    <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="auto-selected-count">
                      {selectedPendingCount} selected
                    </span>
                    {alreadyPassed.length > 0 && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800" title="Skipped — already have a passed attempt">
                        ✓ {alreadyPassed.length} already passed
                      </span>
                    )}
                    {inactiveStaff.length > 0 && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700" title="Skipped — inactive account">
                        ⊘ {inactiveStaff.length} inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setAllPending(true)} data-testid="auto-select-all"
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-amber-500 hover:text-amber-700">Select all</button>
                    <button type="button" onClick={() => setAllPending(false)} data-testid="auto-clear-all"
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-rose-200 hover:text-rose-700">Clear</button>
                  </div>
                </div>
                <div className="mt-2">
                  <Input value={previewQuery} onChange={(e) => setPreviewQuery(e.target.value)}
                    placeholder="Search by name, email, employee ID, department…"
                    data-testid="auto-preview-search" />
                </div>
                <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid="auto-preview-list">
                  {previewLoading ? (
                    <div className="py-8 text-center text-xs text-slate-500">
                      <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading pending staff…
                    </div>
                  ) : filteredPending.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-500">
                      {pendingStaff.length === 0 ? "No pending staff — everyone already has a passed attempt." : "No staff match your search."}
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {filteredPending.map((s) => (
                        <li key={s.user_id} className="flex items-center gap-3 px-3 py-2">
                          <input type="checkbox" checked={!!selectedPending[s.user_id]}
                            onChange={() => togglePending(s.user_id)} className="h-4 w-4"
                            data-testid={`auto-pending-${s.user_id}`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-900 truncate">{s.full_name}</div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {s.email}{s.employee_id ? ` · ${s.employee_id}` : ""}{s.department_name ? ` · ${s.department_name}` : ""}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {autoResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs" data-testid="auto-submit-result">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white">✓ {autoResult.submitted} submitted</span>
                  <span className="rounded-full bg-slate-500 px-2 py-0.5 font-bold text-white">↷ {autoResult.skipped_already_passed} already passed</span>
                  <span className="rounded-full bg-slate-400 px-2 py-0.5 font-bold text-white">↷ {autoResult.skipped_inactive} inactive</span>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-200">
                  {(autoResult.results || []).slice(0, 50).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5">
                      <span className="text-slate-800">{r.user_name || r.user_id?.slice(0, 8)}</span>
                      {r.status === "submitted" ? (
                        <span className={`font-bold ${r.passed ? "text-emerald-700" : "text-rose-700"}`}>
                          {r.percentage}% · {r.passed ? "PASSED" : "FAILED"}
                        </span>
                      ) : (
                        <span className="text-slate-500 italic capitalize">{r.status.replace(/_/g, " ")}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => { setAutoExam(null); setAutoResult(null); }} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">
              {autoResult ? "Close" : "Cancel"}
            </button>
            {!autoResult && (
              <button disabled={autoRunning || selectedPendingCount === 0} onClick={submitAutoBulk}
                className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-70"
                data-testid="auto-submit-confirm">
                {autoRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                Run auto-submit · {selectedPendingCount} staff
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-exam dialog (Institute Admin) */}
      <Dialog open={!!reExamExam} onOpenChange={(o) => { if (!o) { setReExamExam(null); setReEligible([]); setReSelected({}); setReResult(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-sky-600" />
              Re-exam — {reExamExam?.exam_title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-xs text-sky-900">
              <div className="font-bold uppercase tracking-widest mb-1">↺ Re-exam grants a fresh attempt</div>
              Selected staff ke previous attempts <b>archive</b> ho jaayenge (data safe rahega, sirf hidden hoga)
              aur unke auto-issued certificates delete honge. Staff ko is exam par "Start exam" fir se dikhega.
            </div>

            {/* Admin-only power tools: backdate + auto-submit */}
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">
                  Submission date <span className="font-normal normal-case text-slate-400">(optional — reflects on Question Paper PDF, report card & certificate)</span>
                </Label>
                <Input type="date" value={reDate} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setReDate(e.target.value)}
                  data-testid="reexam-date" />
                <div className="text-[11px] text-slate-500">
                  Khali chhodo to aaj ki date use hogi. Only applies if Auto-submit is ON below (manual retake par staff apne submission time se submit karega).
                </div>
              </div>

              <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 cursor-pointer" data-testid="reexam-auto-toggle-wrap">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={reAutoSubmit}
                  onChange={(e) => setReAutoSubmit(e.target.checked)}
                  data-testid="reexam-auto-toggle" />
                <div className="text-xs text-amber-900">
                  <div className="font-bold uppercase tracking-widest">⚡ Auto-submit re-exam attempts</div>
                  ON karne par selected staff ke fresh attempts turant synthetic marks ke saath complete ho jaayenge (bulk auto-submit ki tarah), aur agar exam kisi course se linked hai to certificate bhi auto-issue hoga.
                </div>
              </label>

              {reAutoSubmit && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Min %</Label>
                    <Input type="number" min={1} max={100} value={reRange.min}
                      onChange={(e) => setReRange({ ...reRange, min: e.target.value })}
                      data-testid="reexam-min" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Max %</Label>
                    <Input type="number" min={1} max={100} value={reRange.max}
                      onChange={(e) => setReRange({ ...reRange, max: e.target.value })}
                      data-testid="reexam-max" />
                  </div>
                  <div className="col-span-2 text-[11px] text-slate-500">
                    Default 90–95%. Passing %: <b>{reExamExam?.passing_percentage}%</b>, Total marks: <b>{reExamExam?.total_marks}</b>. Random duration 5–20 min per attempt.
                  </div>
                </div>
              )}
            </div>

            {reResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs" data-testid="reexam-result">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="rounded-full bg-sky-600 px-2 py-0.5 font-bold text-white">↺ {reResult.attempts_archived} archived</span>
                  {reResult.auto_submit && (
                    <>
                      <span className="rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white">✓ {reResult.auto_submit.submitted} submitted</span>
                      <span className="rounded-full bg-slate-500 px-2 py-0.5 font-bold text-white">↷ {reResult.auto_submit.skipped_already_passed} already passed</span>
                      <span className="rounded-full bg-slate-400 px-2 py-0.5 font-bold text-white">↷ {reResult.auto_submit.skipped_inactive} inactive</span>
                    </>
                  )}
                </div>
                {reResult.auto_submit?.results?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto divide-y divide-slate-200">
                    {(reResult.auto_submit.results || []).slice(0, 50).map((r, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5">
                        <span className="text-slate-800">{r.user_name || r.user_id?.slice(0, 8)}</span>
                        {r.status === "submitted" ? (
                          <span className={`font-bold ${r.passed ? "text-emerald-700" : "text-rose-700"}`}>
                            {r.percentage}% · {r.passed ? "PASSED" : "FAILED"}
                          </span>
                        ) : (
                          <span className="text-slate-500 italic capitalize">{r.status.replace(/_/g, " ")}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {reEligLoading ? (
              <div className="py-6 text-center text-xs text-slate-500"><Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading eligible staff…</div>
            ) : reEligible.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-600">
                Is exam par kisi ne bhi attempt nahi diya. Re-exam ki zaroorat nahi.
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-white">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="reexam-select-all"
                      checked={reSelectedCount === reEligible.length}
                      ref={(el) => { if (el) el.indeterminate = reSelectedCount > 0 && reSelectedCount < reEligible.length; }}
                      onChange={(e) => setAllReSel(e.target.checked)}
                    />
                    {reSelectedCount === reEligible.length ? "Deselect all" : "Select all"}
                  </label>
                  <span className="text-[11px] font-semibold text-slate-500" data-testid="reexam-selected-count">
                    {reSelectedCount} / {reEligible.length} selected
                  </span>
                </div>
                <div className="max-h-72 overflow-auto divide-y divide-slate-100">
                  {reEligible.map((s) => (
                    <label key={s.user_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={!!reSelected[s.user_id]} onChange={() => toggleReSel(s.user_id)} data-testid={`reexam-row-${s.user_id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{s.full_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{s.email}{s.login_id ? ` · ${s.login_id}` : ""}{s.department_name ? ` · ${s.department_name}` : ""}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.last_status === "passed" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                        {s.last_status === "passed" ? "PASSED" : "FAILED"} · {(s.last_percentage ?? 0).toFixed?.(1) || s.last_percentage}%
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => { setReExamExam(null); setReResult(null); }} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">
              {reResult ? "Close" : "Cancel"}
            </button>
            {!reResult && (
              <button disabled={reRunning || reSelectedCount === 0} onClick={submitReExam}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:opacity-70 ${reAutoSubmit ? "bg-amber-600 hover:bg-amber-700" : "bg-sky-600 hover:bg-sky-700"}`}
                data-testid="reexam-confirm">
                {reRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (reAutoSubmit ? <Zap className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />)}
                {reAutoSubmit ? `Re-exam + Auto-submit · ${reSelectedCount} staff` : `Allow re-exam · ${reSelectedCount} staff`}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Re-exam Audit Log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-sky-600" />
              Re-exam Audit Log
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
              Har re-exam action ka compliance record — staff name, purana score, naya score,
              backdate, admin. Date-wise sections me PDF me grouped hota hai.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">From date</Label>
                <Input type="date" value={logFrom} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setLogFrom(e.target.value)}
                  data-testid="reexam-log-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">To date</Label>
                <Input type="date" value={logTo} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setLogTo(e.target.value)}
                  data-testid="reexam-log-to" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Exam <span className="font-normal normal-case text-slate-400">(optional — leave blank for all)</span></Label>
              <select value={logExamId} onChange={(e) => setLogExamId(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                data-testid="reexam-log-exam">
                <option value="">All exams</option>
                {exams.map((e) => (
                  <option key={e.id} value={e.id}>{e.exam_title} ({e.exam_code})</option>
                ))}
              </select>
            </div>
            <div className="text-[11px] text-slate-500">
              Khali chhodo to full history download hogi. Dates inclusive hain.
            </div>

            {/* Per-date rows — download PDF/Excel for each date separately */}
            <div className="rounded-md border border-slate-200 bg-white" data-testid="reexam-log-dates">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Date-wise download</span>
                <span className="text-[11px] font-semibold text-slate-500">
                  {logDatesLoading ? "loading…" : `${logDates.length} date(s)`}
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {logDatesLoading ? (
                  <div className="py-6 text-center text-xs text-slate-500">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading dates…
                  </div>
                ) : logDates.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-500">
                    Selected filters ke liye koi re-exam action nahi mila.
                  </div>
                ) : logDates.map((d) => (
                  <div key={d.date} className="flex items-center gap-2 px-3 py-2" data-testid={`reexam-log-date-${d.date}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm text-slate-900">{d.date}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {d.count} staff · {d.auto_submitted_count} auto-submitted
                        {d.exam_titles?.length > 0 ? ` · ${d.exam_titles.slice(0, 2).join(", ")}${d.exam_titles.length > 2 ? ` +${d.exam_titles.length - 2}` : ""}` : ""}
                      </div>
                    </div>
                    <button type="button"
                      disabled={!!logDateBusy[`${d.date}-xlsx`]}
                      onClick={() => downloadReexamLog("xlsx", { date: d.date })}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-500 bg-white px-2 py-1 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      data-testid={`reexam-log-date-xlsx-${d.date}`}
                      title={`Excel for ${d.date}`}>
                      {logDateBusy[`${d.date}-xlsx`] ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                      Excel
                    </button>
                    <button type="button"
                      disabled={!!logDateBusy[`${d.date}-pdf`]}
                      onClick={() => downloadReexamLog("pdf", { date: d.date })}
                      className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1 text-[10px] font-bold text-white hover:bg-sky-700 disabled:opacity-60"
                      data-testid={`reexam-log-date-pdf-${d.date}`}
                      title={`PDF for ${d.date}`}>
                      {logDateBusy[`${d.date}-pdf`] ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                      PDF
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <button onClick={() => setLogOpen(false)}
              className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">
              Close
            </button>
            <button disabled={logBusy.xlsx} onClick={() => downloadReexamLog("xlsx")}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-70"
              data-testid="reexam-log-xlsx">
              {logBusy.xlsx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
              All Excel
            </button>
            <button disabled={logBusy.pdf} onClick={() => downloadReexamLog("pdf")}
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-4 py-2 text-xs font-bold text-white hover:bg-sky-700 disabled:opacity-70"
              data-testid="reexam-log-pdf">
              {logBusy.pdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              All PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
export function OrgExamAssignmentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/org/exam-assignments");
        setRows(data.data.assignments);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DashboardLayout title="Exam Assignments" breadcrumbs={["Hospital Admin", "Exam Assignments"]}>
      <div className="space-y-4" data-testid="org-assignments">
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No assignments" description="Assign exams to your staff first." icon={ClipboardList} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Staff</th><th className="px-4 py-3 text-left">Exam</th><th className="px-4 py-3 text-left">Assigned</th><th className="px-4 py-3 text-left">Due</th><th className="px-4 py-3 text-left">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2"><div className="font-medium">{r.user?.full_name}</div><div className="text-xs text-slate-500">{r.user?.email}</div></td>
                    <td className="px-4 py-2"><div>{r.exam?.exam_title}</div><div className="font-mono text-xs text-slate-500">{r.exam?.exam_code}</div></td>
                    <td className="px-4 py-2 text-xs">{r.assigned_at?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs">{r.due_date || "—"}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// HOSPITAL ADMIN - Exam Results
// =========================================================================
export function OrgExamResultsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/org/exam-results");
        setRows(data.data.attempts);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DashboardLayout title="Staff Exam Results" breadcrumbs={["Hospital Admin", "Results"]}>
      <div className="space-y-4" data-testid="org-results">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={async () => {
            try {
              const { data } = await api.get("/org/exam-results/export", { params: { format: "xlsx" }, responseType: "blob" });
              const url = URL.createObjectURL(new Blob([data]));
              const link = document.createElement("a");
              link.href = url;
              link.download = "staff_exam_results.xlsx";
              link.click();
              URL.revokeObjectURL(url);
            } catch (err) { toast.error(formatApiError(err)); }
          }} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="btn-org-export">
            Export XLSX
          </button>
          <button onClick={async () => {
            try {
              const { data } = await api.get("/org/exam-results/export", { params: { format: "pdf" }, responseType: "blob" });
              const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
              const link = document.createElement("a");
              link.href = url;
              link.download = "staff_exam_results.pdf";
              link.click();
              URL.revokeObjectURL(url);
            } catch (err) { toast.error(formatApiError(err)); }
          }} className="rounded-md border-2 border-rose-500 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700 hover:bg-rose-100" data-testid="btn-org-export-pdf">
            Export PDF
          </button>
        </div>
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No results yet" description="Results will appear when staff attempt exams." icon={Award} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Staff</th><th className="px-4 py-3 text-left">Exam</th><th className="px-4 py-3 text-left">#</th><th className="px-4 py-3 text-left">Score</th><th className="px-4 py-3 text-left">%</th><th className="px-4 py-3 text-left">Result</th><th className="px-4 py-3 text-right">PDF</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 font-medium">{r.user?.full_name}<div className="text-xs text-slate-500">{r.user?.email}</div></td>
                    <td className="px-4 py-2 text-xs">{r.exam?.exam_title}</td>
                    <td className="px-4 py-2 text-xs">#{r.attempt_number}</td>
                    <td className="px-4 py-2 text-xs">{r.marks_obtained}/{r.total_marks}</td>
                    <td className="px-4 py-2 text-xs font-bold">{r.percentage}%</td>
                    <td className="px-4 py-2"><StatusBadge status={r.pass_fail_status || r.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={async () => {
                          try {
                            const { data } = await api.get(`/org/exam-attempts/${r.id}/summary-report.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `report_${r.exam?.exam_code || 'exam'}_${(r.user?.full_name || 'staff').replace(/\s+/g, '_')}.pdf`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }} className="rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-emerald-700" data-testid={`org-summary-pdf-${r.id}`}>
                          Report
                        </button>
                        <button onClick={async () => {
                          try {
                            const { data } = await api.get(`/org/exam-attempts/${r.id}/question-paper.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `question_paper_${r.exam?.exam_code || 'exam'}_${(r.user?.full_name || 'staff').replace(/\s+/g, '_')}.pdf`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }} className="rounded-md bg-[#101876] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#0c135c]" data-testid={`org-paper-pdf-${r.id}`}>
                          Paper
                        </button>
                        {(r.auto_submitted || r.admin_auto_submitted) && (
                          <button onClick={async () => {
                            if (!window.confirm("Reshuffle the auto-generated question paper for this staff? The old set will be replaced.")) return;
                            try {
                              await api.post(`/org/exam-attempts/${r.id}/regenerate-paper`);
                              toast.success("Question paper regenerated. Download 'Paper' again to see the new set.");
                            } catch (err) { toast.error(formatApiError(err)); }
                          }} className="rounded-md bg-amber-500 px-2 py-1 text-[11px] font-bold text-white hover:bg-amber-600" data-testid={`org-regen-paper-${r.id}`} title="Reshuffle auto-generated paper">
                            Regen
                          </button>
                        )}
                        <button onClick={async () => {
                          try {
                            const { data } = await api.get(`/org-admin/exam-attempts/${r.id}/pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `attempt_${r.attempt_number}.pdf`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }} className="text-[11px] font-bold text-[#101876] hover:underline" data-testid={`org-pdf-${r.id}`}>Q&amp;A</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// FACULTY - Evaluation Pending / Detail
// =========================================================================
export function FacultyEvaluationPendingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/faculty/evaluation/pending");
        setRows(data.data.attempts);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DashboardLayout title="Pending Evaluation" breadcrumbs={["Faculty", "Evaluation"]}>
      <div className="space-y-4" data-testid="faculty-evaluation">
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No pending evaluations" description="Descriptive answers awaiting your review will appear here." icon={ClipboardList} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Learner</th><th className="px-4 py-3 text-left">Attempt</th><th className="px-4 py-3 text-left">Submitted</th><th className="px-4 py-3 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 font-medium">{r.user?.full_name}<div className="text-xs text-slate-500">{r.user?.email}</div></td>
                    <td className="px-4 py-2 text-xs">#{r.attempt_number}</td>
                    <td className="px-4 py-2 text-xs">{r.submitted_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/app/faculty/evaluation/${r.id}`} className="text-xs font-bold text-[#101876] hover:underline">Evaluate →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function FacultyEvaluationDetailPage() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/faculty/evaluation/attempts/${attemptId}`);
      setData(r.data);
      const s = {}; const c = {};
      r.data.answers.forEach((a) => { s[a.answer_id] = a.marks_awarded ?? ""; c[a.answer_id] = a.comments ?? ""; });
      setScores(s); setComments(c);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [attemptId]);
  useEffect(() => { load(); }, [load]);

  const saveOne = async (aid) => {
    try {
      await api.post(`/faculty/evaluation/answers/${aid}/evaluate`, {
        marks_awarded: parseFloat(scores[aid]) || 0,
        comments: comments[aid] || "",
      });
      toast.success("Saved.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const publish = async () => {
    try {
      await api.post(`/faculty/evaluation/attempts/${attemptId}/publish-result`);
      toast.success("Result published.");
      nav("/app/faculty/evaluation");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <DashboardLayout title="Evaluate"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Evaluate descriptive answers" breadcrumbs={["Faculty", "Evaluation"]}>
      <div className="max-w-4xl space-y-4" data-testid="faculty-eval-detail">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        {data.answers.length === 0 ? (
          <EmptyState title="No descriptive answers" description="This attempt has no manually-evaluated questions." icon={ClipboardList} />
        ) : (
          data.answers.map((a) => (
            <div key={a.answer_id} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
              <div>
                <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">Q{a.question_order} · max {a.max_marks} marks</div>
                <div className="mt-1 text-sm font-semibold">{a.question_text}</div>
              </div>
              <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-line">{a.descriptive_answer || <em>No answer</em>}</div>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" min="0" max={a.max_marks} value={scores[a.answer_id] || ""} onChange={(e) => setScores((p) => ({ ...p, [a.answer_id]: e.target.value }))}
                  placeholder="Marks" className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`eval-marks-${a.answer_id}`} />
                <input value={comments[a.answer_id] || ""} onChange={(e) => setComments((p) => ({ ...p, [a.answer_id]: e.target.value }))}
                  placeholder="Comments (optional)" className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <button onClick={() => saveOne(a.answer_id)} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid={`eval-save-${a.answer_id}`}>Save marks</button>
            </div>
          ))
        )}
        {data.answers.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <button onClick={publish} className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700" data-testid="eval-publish">Publish final result</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
